import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, TrendingUp, TrendingDown, Clock, Activity } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart, ReferenceLine } from 'recharts';

import { API } from "../config/api";

const IntradayPriceAction = ({ ticker }) => {
  const [priceData, setPriceData] = useState(null);
  const [premarket, setPremarket] = useState(null); // premarket H/L snapshot
  const [loading, setLoading] = useState(false);
  const [lastFetchedTicker, setLastFetchedTicker] = useState(null);

  // Clear data when ticker changes to show loading state
  useEffect(() => {
    if (ticker !== lastFetchedTicker) {
      setPriceData(null);
    }
  }, [ticker, lastFetchedTicker]);

  const fetchPriceAction = useCallback(async (tickerToFetch) => {
    if (!tickerToFetch) return;

    setLoading(true);
    try {
      // Fetch intraday price action + premarket snapshot in parallel.
      // The premarket H/L will render as horizontal ReferenceLines on
      // the chart so users can spot ORB breakouts at-a-glance.
      const [resp, pmResp] = await Promise.allSettled([
        axios.get(`${API}/stock/intraday-price-action/${tickerToFetch}`),
        axios.get(`${API}/stock/premarket-snapshot/${tickerToFetch}`, { timeout: 12000 }),
      ]);
      if (resp.status === "fulfilled") {
        setPriceData(resp.value.data);
        setLastFetchedTicker(tickerToFetch);
      }
      setPremarket(pmResp.status === "fulfilled" ? pmResp.value.data : null);
    } catch (error) {
      console.error("Error fetching intraday price action:", error);
      console.warn("Failed to load intraday price data - retrying silently in background");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ticker) return;

    // Fetch immediately when ticker changes
    fetchPriceAction(ticker);
    
    // Auto-refresh every 30 seconds for up-to-the-minute data
    const interval = setInterval(() => fetchPriceAction(ticker), 30000);
    
    // Cleanup interval on unmount or ticker change
    return () => clearInterval(interval);
  }, [ticker, fetchPriceAction]);

  const getChangeColor = (change) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-slate-600';
  };

  const getChangeBgColor = (change) => {
    if (change > 0) return 'bg-green-100 border-green-500';
    if (change < 0) return 'bg-red-100 border-red-500';
    return 'bg-slate-100 border-slate-400';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'uptrend') return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (trend === 'downtrend') return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Activity className="w-4 h-4 text-slate-600" />;
  };

  const getSessionColor = (session) => {
    if (session === 'opening') return 'bg-blue-500 text-white';
    if (session === 'power_hour') return 'bg-purple-500 text-white';
    if (session === 'after_hours') return 'bg-slate-500 text-white';
    return 'bg-green-500 text-white';
  };

  return (
    <Card className="border-slate-200 bg-white/80 backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-indigo-600" />
            <CardTitle>Today&apos;s Price Action</CardTitle>
          </div>
          {priceData && (
            <div className="flex items-center gap-3 text-xs">
              <Badge className={getSessionColor(priceData.session)}>
                {(priceData.session || '').replace('_', ' ').toUpperCase()}
              </Badge>
              <span className="text-slate-600">
                Last update: {priceData.last_update.split(' ')[1]}
              </span>
            </div>
          )}
        </div>
        <p className="text-xs text-slate-600">
          Real-time intraday price chart with {priceData?.interval === '5m' ? '5-minute' : '1-minute'} intervals
        </p>
      </CardHeader>
      <CardContent>
        {loading && !priceData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            <span className="ml-2 text-sm text-slate-600">Loading today&apos;s price action...</span>
          </div>
        ) : priceData ? (
          <div className="space-y-6">
            {/* Price Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-600 mb-1">Open</p>
                <p className="text-lg font-bold text-slate-900">${priceData.summary?.open}</p>
              </div>
              
              <div className={`p-4 rounded-lg border-2 ${getChangeBgColor(priceData.summary?.change_pct)}`}>
                <p className="text-xs text-slate-700 mb-1">Current</p>
                <p className="text-lg font-bold text-slate-900">${priceData.summary?.current || '0.00'}</p>
                <p className={`text-xs font-semibold ${getChangeColor(priceData.summary?.change_pct)}`}>
                  {(priceData.summary?.change || 0) > 0 ? '+' : ''}{priceData.summary?.change?.toFixed(2) || '0.00'} 
                  ({(priceData.summary?.change_pct || 0) > 0 ? '+' : ''}{priceData.summary?.change_pct?.toFixed(2) || '0.00'}%)
                </p>
              </div>
              
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-xs text-green-700 mb-1">High</p>
                <p className="text-lg font-bold text-green-900">${priceData.summary?.high}</p>
                <p className="text-xs text-slate-600">{priceData.key_levels?.high_time}</p>
              </div>
              
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-xs text-red-700 mb-1">Low</p>
                <p className="text-lg font-bold text-red-900">${priceData.summary?.low}</p>
                <p className="text-xs text-slate-600">{priceData.key_levels?.low_time}</p>
              </div>
              
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-xs text-purple-700 mb-1">Volume</p>
                <p className="text-sm font-bold text-purple-900">
                  {((priceData.summary?.volume || 0) / 1000000).toFixed?.(2) || "0.00"}M
                </p>
                <p className="text-xs text-slate-600">Volatility: {priceData.summary?.volatility_pct?.toFixed(2) || '0.00'}%</p>
              </div>
            </div>

            {/* Trend and Position */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  {getTrendIcon(priceData.trend?.direction)}
                  <h4 className="text-sm font-semibold text-slate-900">Intraday Trend</h4>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={
                    priceData.trend?.direction === 'uptrend' ? 'border-green-500 text-green-700' :
                    priceData.trend?.direction === 'downtrend' ? 'border-red-500 text-red-700' :
                    'border-slate-400 text-slate-700'
                  }>
                    {priceData.trend?.direction?.toUpperCase() || 'UNKNOWN'}
                  </Badge>
                  {priceData.trend?.strength && (
                    <span className="text-xs text-slate-600">
                      Strength: {((priceData.trend?.strength || 0) * 100).toFixed?.(1) || "0.0"}%
                    </span>
                  )}
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-200">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">Position in Range</h4>
                <div className="space-y-2">
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500"
                      style={{ width: `${priceData.key_levels?.position_in_range_pct || 50}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-red-600 font-semibold">${priceData.key_levels?.support || '0.00'}</span>
                    <span className="text-slate-700">{priceData.key_levels?.position_in_range_pct?.toFixed(1) || '0.0'}%</span>
                    <span className="text-green-600 font-semibold">${priceData.key_levels?.resistance || '0.00'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Price Chart */}
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-3">
                Intraday Price Movement ({priceData.data_points} data points)
              </h4>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={priceData.chart_data} margin={{ top: 10, right: 30, left: 0, bottom: 30 }}>
                  <defs>
                    <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="time" 
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={Math.floor(priceData.chart_data.length / 12)}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    domain={['auto', 'auto']}
                    label={{ value: 'Price ($)', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '12px' }}
                    formatter={(value, name) => {
                      if (name === 'price') return [`$${value}`, 'Price'];
                      if (name === 'volume') return [value.toLocaleString(), 'Volume'];
                      return [value, name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  
                  {/* Reference line for opening price */}
                  <ReferenceLine 
                    y={priceData.summary?.open} 
                    stroke="#64748b" 
                    strokeDasharray="5 5"
                    label={{ value: `Open: $${priceData.summary?.open}`, fontSize: 10, fill: '#64748b' }}
                  />

                  {/* Premarket H/L reference lines — white dotted so they
                      read as session boundaries (not "alerts"). Lets traders
                      see at-a-glance where current price sits relative to
                      the premarket range — critical for ORB plays. */}
                  {premarket?.ready && (
                    <>
                      <ReferenceLine
                        y={premarket.premarket_high}
                        stroke="#ffffff"
                        strokeWidth={1.5}
                        strokeDasharray="2 4"
                        strokeOpacity={0.9}
                        ifOverflow="extendDomain"
                        label={{
                          value: `PM High: $${premarket.premarket_high}`,
                          fontSize: 10,
                          fill: '#ffffff',
                          position: 'right',
                        }}
                      />
                      <ReferenceLine
                        y={premarket.premarket_low}
                        stroke="#ffffff"
                        strokeWidth={1.5}
                        strokeDasharray="2 4"
                        strokeOpacity={0.9}
                        ifOverflow="extendDomain"
                        label={{
                          value: `PM Low: $${premarket.premarket_low}`,
                          fontSize: 10,
                          fill: '#ffffff',
                          position: 'right',
                        }}
                      />
                    </>
                  )}
                  
                  <Area 
                    type="monotone" 
                    dataKey="price" 
                    stroke="#6366f1" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorPrice)" 
                    name="Price"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Additional Info */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h5 className="text-sm font-semibold text-slate-900 mb-2">Key Insights</h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-slate-700">
                <div>
                  <span className="font-semibold">Price Range: </span>
                  ${(((priceData.summary?.high || 0) - (priceData.summary?.low || 0))).toFixed?.(2) || "0.00"} 
                  ({priceData.summary?.low ? (((priceData.summary?.high - priceData.summary?.low) / priceData.summary?.low * 100).toFixed(2)) : '0.00'}%)
                </div>
                <div>
                  <span className="font-semibold">Intraday Volatility: </span>
                  {priceData.summary?.volatility_pct?.toFixed(2) || '0.00'}%
                  {(priceData.summary?.volatility_pct || 0) > 2 ? ' (High)' : (priceData.summary?.volatility_pct || 0) < 0.5 ? ' (Low)' : ' (Normal)'}
                </div>
                <div>
                  <span className="font-semibold">Data Interval: </span>
                  {priceData.interval === '5m' ? '5 minutes' : '1 minute'}
                  {' '}(auto-refreshes every minute)
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-slate-600 py-8">No intraday price data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default IntradayPriceAction;
